/**
 * Input primitive - Direction A "Tu phap restraint"
 *
 * 2 variant: default | search (with leading magnifier icon)
 * State: default | focus (navy ring) | error (red border) | disabled
 * Size: md 40px default, lg 48px
 * Touch target >= 44x44px on mobile.
 *
 * A11y:
 *  - aria-label OR explicit <label> required (caller responsibility)
 *  - aria-invalid=true khi error
 *  - aria-describedby tro vao error message id (caller provide)
 *  - error message render inline neu `error` truyen string
 *
 * Phase 1 Track 2 (2026-05-22).
 */

import * as React from 'react';

export type InputVariant = 'default' | 'search';
export type InputSize = 'md' | 'lg';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  variant?: InputVariant;
  inputSize?: InputSize;
  error?: string | boolean;
  /** Custom leading icon (overrides default magnifier for variant=search). */
  icon?: React.ReactNode;
  /** Optional error message id for aria-describedby wiring. */
  errorId?: string;
}

const SearchIcon = (): React.ReactElement => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  function Input(
    {
      variant = 'default',
      inputSize = 'md',
      error,
      icon,
      errorId,
      className,
      type = 'text',
      'aria-describedby': ariaDescribedBy,
      ...rest
    },
    ref
  ) {
    const hasError = Boolean(error);
    const errorText = typeof error === 'string' ? error : '';
    const autoErrorId =
      errorId ||
      (hasError && errorText
        ? `ask-input-err-${Math.random().toString(36).slice(2, 8)}`
        : undefined);
    const describedBy = [ariaDescribedBy, autoErrorId]
      .filter(Boolean)
      .join(' ') || undefined;

    const cls = [
      'ask-input',
      variant === 'search' ? 'ask-input--search' : '',
      inputSize === 'lg' ? 'ask-input--lg' : '',
      hasError ? 'ask-input--error' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const leadingIcon =
      variant === 'search' ? icon || <SearchIcon /> : icon || null;

    return (
      <span className="ask-input-wrap">
        {leadingIcon ? (
          <span className="ask-input-wrap__icon">{leadingIcon}</span>
        ) : null}
        <input
          ref={ref}
          type={type}
          className={cls}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
          {...rest}
        />
        {hasError && errorText ? (
          <span
            id={autoErrorId}
            className="ask-input-error-msg"
            role="alert"
          >
            {errorText}
          </span>
        ) : null}
      </span>
    );
  }
);
